import React, { useMemo, useState } from 'react';

import { colorForQuestionOption } from '@aline/common';
import {
  Autocomplete,
  DialogActions,
  DialogContent,
  IconButton,
  InputBase,
  List,
  ListItem,
  TextField,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import Papa from 'papaparse';
import {
  AlineAutocompleteInput,
  AlineButton,
  AlineCheckbox,
  AlineDialog,
  AlineTextarea,
  questionMenuItemBorderRadius,
  Spacer,
} from 'src/components';
import { usePreserveAutocompleteSearchInput } from 'src/components/generic/data-grid/autocomplete-filter-utils';
import { IconColors } from 'src/components/icons/icon-utils';
import { SearchIcon } from 'src/components/icons/SearchIcon';
import { SkinnyPlusIcon } from 'src/components/icons/SkinnyPlusIcon';
import { SmallTrashCanIcon } from 'src/components/icons/SmallTrashCanIcon';
import { Apollo } from 'src/gql/generated';
import {
  useAddQuestionOptionsOptimistic,
  useDeleteQuestionOptionOptimistic,
  useGetExtractionQuestion,
} from 'src/hooks';
import { alineLinkButtonStylesWithUnderline, getTypographyForSx } from 'src/theme';
import tinycolor from 'tinycolor2';
import { makeStyles } from 'tss-react/mui';

const useOptionsManagerStyles = makeStyles()(theme => ({
  addOptionContainer: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    borderRadius: questionMenuItemBorderRadius,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
  },
  addOptionText: {
    fontWeight: 600,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  csvDialogActions: {
    paddingTop: theme.spacing(3),
  },
  csvDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 420,
  },
  csvLink: {
    ...alineLinkButtonStylesWithUnderline,
    fontSize: 12,
  },
  csvLinkDisabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  csvTextarea: {
    width: '100%',
  },
  emptyOptions: {
    color: theme.appColors.secondaryType,
    padding: theme.spacing(0.75, 1),
  },
  helperText: {
    color: theme.appColors.secondaryType,
  },
  inputBase: {
    padding: 0,
  },
  optionColor: {
    alignSelf: 'center',
    borderRadius: 6,
    flex: '0 0 auto',
    height: 16,
    marginRight: theme.spacing(1),
    width: 16,
  },
  optionList: {
    padding: 0,
  },
  optionsHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1, 0.25),
  },
  optionsLabel: {
    ...theme.appTypography.fieldLabel,
  },
  searchContainer: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    borderRadius: questionMenuItemBorderRadius,
    display: 'flex',
    padding: theme.spacing(0.5, 1),
  },
  searchInput: {
    marginLeft: theme.spacing(1),
  },
  selectedOption: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    borderRadius: questionMenuItemBorderRadius,
    display: 'flex',
    padding: theme.spacing(0.75, 1),
  },
}));

interface ExtractionQuestionOptionsManagerProps {
  question: Pick<Apollo.ExtractionQuestion, 'id' | 'options' | 'type' | '__typename'>;
  disabled?: boolean;
}

export function ExtractionQuestionOptionsManager({
  disabled = false,
  question,
}: ExtractionQuestionOptionsManagerProps): JSX.Element | null {
  const [csvText, setCsvText] = useState('');
  const { classes } = useOptionsManagerStyles();
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { addQuestionOptionsOptimistic, loading: addingOptions } = useAddQuestionOptionsOptimistic(
    question.id
  );
  const { deleteQuestionOptionOptimistic } = useDeleteQuestionOptionOptimistic(question.id);
  const csvOptionLabels = useMemo(
    () => parseNewOptionLabels(csvText, question.options),
    [csvText, question.options]
  );
  const csvOptions = useMemo(
    () => createQuestionOptionInputs(csvOptionLabels, question.options.length),
    [csvOptionLabels, question.options.length]
  );
  const filteredQuestionOptions = useMemo(() => {
    if (!searchText) return question.options;

    return question.options.filter(option =>
      option.label.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [question.options, searchText]);

  const handleAddOptions = async (options: Apollo.QuestionOptionInput[]) => {
    if (!options.length) return;

    await addQuestionOptionsOptimistic({
      variables: {
        input: {
          options,
          questionId: question.id,
        },
      },
    });
  };

  const handleAddOption = async (optionText: string) => {
    if (!optionText) return;
    if (!question.options.some(option => option.label.toLowerCase() === optionText.toLowerCase())) {
      setSearchText('');
      await handleAddOptions(createQuestionOptionInputs([optionText], question.options.length));
    }
  };

  const handleAddCsvOptions = async () => {
    await handleAddOptions(csvOptions);
    setCsvText('');
    setCsvDialogOpen(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    const optionText = searchText.trim();
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption(optionText);
    }
  };

  if (
    question.type !== Apollo.ExtractionQuestionType.SingleSelect &&
    question.type !== Apollo.ExtractionQuestionType.MultiSelect
  ) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div className={classes.searchContainer}>
        <SearchIcon />
        <InputBase
          fullWidth
          className={classes.searchInput}
          classes={{ input: classes.inputBase }}
          disabled={disabled}
          placeholder="Type to add options..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onKeyDown={handleKeyPress}
        />
      </div>
      <div className={classes.optionsHeader}>
        <Typography className={classes.optionsLabel}>Options</Typography>
        <Typography
          className={clsx(classes.csvLink, disabled && classes.csvLinkDisabled)}
          onClick={() => {
            if (disabled) return;

            setCsvDialogOpen(true);
          }}
        >
          Add CSV Options
        </Typography>
      </div>
      {Boolean(filteredQuestionOptions.length) && (
        <List className={classes.optionList}>
          {filteredQuestionOptions.map(option => (
            <OptionItem
              key={option.id}
              disabled={disabled}
              option={option}
              onClick={async () => {
                await deleteQuestionOptionOptimistic({
                  variables: {
                    optionId: option.id,
                  },
                });
              }}
            />
          ))}
        </List>
      )}
      {!question.options.length && (
        <Typography align="center" className={classes.emptyOptions} variant="body1">
          No Options
        </Typography>
      )}
      {searchText.trim() &&
        !question.options.some(
          option => option.label.toLowerCase() === searchText.trim().toLowerCase()
        ) && (
          <div
            className={classes.addOptionContainer}
            onClick={() => handleAddOption(searchText.trim())}
          >
            <SkinnyPlusIcon fill={IconColors.Primary} height={14} width={14} />
            <Typography
              noWrap
              className={classes.addOptionText}
              color="primary"
            >{`Create "${searchText.trim()}" option`}</Typography>
          </div>
        )}
      <AlineDialog
        open={csvDialogOpen}
        title="Add Options from CSV"
        onClose={() => setCsvDialogOpen(false)}
      >
        <DialogContent className={classes.csvDialogContent}>
          <AlineTextarea
            className={classes.csvTextarea}
            disabled={disabled}
            maxRows={8}
            minRows={5}
            placeholder="Add a list of options separated by semicolons or line breaks..."
            value={csvText}
            onChange={e => setCsvText(e.target.value)}
          />
        </DialogContent>
        <DialogActions className={classes.csvDialogActions}>
          <AlineButton color="primary" variant="outlined" onClick={() => setCsvDialogOpen(false)}>
            Cancel
          </AlineButton>
          <AlineButton
            color="primary"
            disabled={!csvOptions.length || disabled}
            loading={addingOptions}
            variant="contained"
            onClick={handleAddCsvOptions}
          >
            Add Options
          </AlineButton>
        </DialogActions>
      </AlineDialog>
    </div>
  );
}

function createQuestionOptionInputs(labels: string[], startOrder: number) {
  return labels.map((label, index) => {
    const order = startOrder + index;

    return {
      color: colorForQuestionOption(order),
      label,
      order,
    };
  });
}

function parseNewOptionLabels(
  value: string,
  existingOptions: Pick<Apollo.QuestionOption, 'label' | '__typename'>[]
) {
  const labels: string[] = [];
  const { data } = Papa.parse<string[]>(value, { delimiter: ';' });
  const seenLabels = new Set(existingOptions.map(option => normalizeOptionLabel(option.label)));

  for (const row of data) {
    for (const optionText of row) {
      const label = optionText.trim();
      if (!label) continue;

      const normalizedLabel = normalizeOptionLabel(label);
      if (seenLabels.has(normalizedLabel)) continue;

      seenLabels.add(normalizedLabel);
      labels.push(label);
    }
  }

  return labels;
}

function normalizeOptionLabel(label: string) {
  return label.toLowerCase();
}

function OptionItem({
  disabled,
  onClick,
  option,
}: {
  onClick: (e: React.MouseEvent) => void;
  option: Pick<Apollo.QuestionOption, 'id' | 'label' | 'color' | '__typename'>;
  disabled?: boolean;
}): JSX.Element {
  const { classes } = useOptionsManagerStyles();
  const [showDelete, setShowDelete] = useState(false);

  return (
    <ListItem
      className={classes.selectedOption}
      onMouseEnter={() => setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
    >
      <span className={classes.optionColor} style={{ backgroundColor: option.color }} />
      <Typography noWrap>{option.label}</Typography>
      <Spacer />
      <IconButton
        disabled={disabled}
        edge="end"
        size="small"
        style={{ visibility: showDelete ? 'visible' : 'hidden' }}
        onClick={onClick}
      >
        <SmallTrashCanIcon />
      </IconButton>
    </ListItem>
  );
}

export const denseOptionChipGeometry = {
  borderRadius: 10,
  fontSize: 10,
  /** Room for descenders: the chip label is clipped, not just wrapped. */
  lineHeight: 1.2,
} as const;

const useChipStyles = makeStyles<void, 'chipText'>()((theme, _params, classes) => ({
  chip: {
    alignItems: 'center',
    borderRadius: 12,
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 1),
  },
  /** For chips inline with body text, where the standard pill dominates. */
  chipDense: {
    [`& .${classes.chipText}`]: {
      fontSize: denseOptionChipGeometry.fontSize,
      lineHeight: denseOptionChipGeometry.lineHeight,
    },
    borderRadius: denseOptionChipGeometry.borderRadius,
    padding: theme.spacing(0.25, 0.75),
  },
  chipText: {
    ...getTypographyForSx(theme.typography.body1),
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
  },
}));

export function OptionItemChip({
  className,
  dense = false,
  option,
  ...rest
}: {
  option: Partial<Pick<Apollo.QuestionOption, 'color' | 'label' | '__typename'>>;
  className?: string;
  dense?: boolean;
}): JSX.Element {
  const { classes } = useChipStyles();
  const base = tinycolor(option.color);

  return (
    <div
      className={clsx(classes.chip, dense && classes.chipDense, className)}
      style={{
        backgroundColor: base.clone().setAlpha(0.2).toRgbString(),
        border: `1px solid ${base.clone().setAlpha(0.5).toRgbString()}`,
      }}
      {...rest}
    >
      <Typography
        noWrap
        className={classes.chipText}
        style={{
          color: base.isLight()
            ? base.clone().darken(30).toRgbString()
            : base.clone().lighten(30).toRgbString(),
        }}
      >
        {option.label}
      </Typography>
    </div>
  );
}

const useAutocompleteStyles = makeStyles()(theme => ({
  checkbox: {
    padding: 0,
  },
  chip: {
    marginRight: theme.spacing(0.5),
  },
}));

export type AutocompleteQuestionOption = Pick<
  Apollo.QuestionOption,
  'id' | 'label' | 'color' | 'order' | '__typename'
>;

/**
 * Autocomplete for question options fetched by questionId.
 * - Renders options with an AlineCheckbox and OptionItemChip
 * - Supports multi-select for MultiSelect questions, single-select for SingleSelect
 */
export function QuestionOptionsAutocomplete({
  className,
  disabled,
  inputType = 'mui',
  multiple = false,
  onChange,
  questionId,
  value,
}: {
  multiple: boolean;
  onChange: (selected: AutocompleteQuestionOption[]) => void;
  questionId: string;
  className?: string;
  disabled?: boolean;
  inputType?: 'aline' | 'mui';
  value?: string[];
}): JSX.Element | null {
  const { classes } = useAutocompleteStyles();
  const { extractionQuestion } = useGetExtractionQuestion(questionId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const options = extractionQuestion?.options ?? [];
  const selectedOptions = options.filter(opt => value?.includes(opt.label));
  const { inputValue, onInputChange } = usePreserveAutocompleteSearchInput();

  return (
    <Autocomplete<AutocompleteQuestionOption, true>
      // Coerce to multiple so we always show chips in renderValue (which is skipped when multiple === false)...
      multiple
      className={className}
      disableCloseOnSelect={multiple}
      getOptionLabel={opt => opt.label}
      inputValue={multiple ? inputValue : undefined}
      options={options}
      renderInput={params => {
        if (inputType === 'aline')
          return <AlineAutocompleteInput {...params} placeholder="Select option(s)..." />;

        return (
          <TextField
            {...params}
            disabled={disabled}
            placeholder="Select option(s)..."
            variant="standard"
          />
        );
      }}
      renderOption={(props, opt, state) => {
        const { key, ...optionProps } = props;

        return (
          <li key={key} {...optionProps}>
            <div style={{ alignItems: 'center', display: 'flex', gap: 8 }}>
              <AlineCheckbox
                checked={state.selected}
                className={classes.checkbox}
                disabled={disabled}
              />
              <OptionItemChip option={opt} />
            </div>
          </li>
        );
      }}
      renderValue={(options, getItemProps) => {
        const sortedOptions = [...options].sort((a, b) => a.order - b.order);
        return sortedOptions.map((opt, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return <OptionItemChip key={key} option={opt} {...itemProps} />;
        });
      }}
      value={selectedOptions}
      onChange={(_, newSelectedOptions) => {
        const lastOption = newSelectedOptions.at(-1);
        const nextOptions = multiple ? newSelectedOptions : lastOption ? [lastOption] : [];
        onChange(nextOptions);
      }}
      onInputChange={multiple ? onInputChange : undefined}
    />
  );
}
